type DatasetInfo = {
  displayName: string;
  shortName: string;
  domoName: string;
  description: string;
  availableToLLM: boolean;
  availableToUser: boolean; // Whether this appears in the dataset selector UI
};

export type DatasetName =
  | "main"
  | "benchmark"
  | "attrition_drivers"
  | "attrition_directional_drivers"
  | "methodology"
  | "recs"
  | "recruitment_applications"
  | "time_to_fill_drivers"
  | "faq"
  | "culture"
  | "employee_relations"
  | "osha_incidents"
  | "employee_goals"
  | "lightcast";

export const DATASETS: Record<DatasetName, DatasetInfo> = {
  main: {
    displayName: "HRIS Data",
    shortName: "HRIS",
    domoName:
      "View [Console]PROD for Query Bar | Stage4|Employee Data|Daily|Viz",
    description:
      "Main dataset containing employee information for one or more companies including demographics, organizational data, compensation, and employment details.",
    availableToLLM: true,
    availableToUser: true,
  },
  recruitment_applications: {
    displayName: "Recruitment",
    shortName: "Recruitment",
    domoName: "View of PROD | Applications",
    description:
      "Dataset containing job application details including applicant demographics, application status, recruiter, recruitment stage, and other relevant information.",
    availableToLLM: true,
    availableToUser: true,
  },
  benchmark: {
    displayName: "Benchmark Data",
    shortName: "Benchmark",
    domoName:
      "[NP] PROD | Stage 5(B) | Benchmarking Data Integrated with Company Representation | Race & Gender",
    description:
      "Benchmarking data for HR leaders to compare their company against industry norms.",
    availableToLLM: true,
    availableToUser: true,
  },
  attrition_drivers: {
    displayName: "Attrition Drivers",
    shortName: "Attrition Drivers",
    domoName: "Console_Top_Drivers_v1",
    description:
      "Dataset containing analysis of factors contributing to employee attrition and their relative impact.",
    availableToLLM: true,
    availableToUser: false, // this is set to false because the attrition_drivers measure is added artificially to the HRIS dataset
  },
  attrition_directional_drivers: {
    displayName: "Directional Drivers",
    shortName: "Directional Drivers",
    domoName: "Attrition Directional Drivers",
    description:
      "Dataset showing factors that increase or decrease attrition, with their directional influence values.",
    availableToLLM: false,
    availableToUser: false,
  },
  methodology: {
    displayName: "Methodology",
    shortName: "Methodology",
    domoName: "View of Methodology",
    description:
      "Dataset detailing the methodologies used in analyses and reports.",
    availableToLLM: false,
    availableToUser: false,
  },
  recs: {
    displayName: "Recommendations",
    shortName: "Recommendations",
    domoName: "Console_Recs_v2",
    description:
      "List of recommended actions for HR leaders to improve their business.",
    availableToLLM: false,
    availableToUser: false,
  },
  time_to_fill_drivers: {
    displayName: "Top Drivers of Longer Time to Fill",
    shortName: "Time to Fill Drivers",
    domoName: "View of PROD | Recruitment Directional Drivers",
    description:
      "Dataset showing factors that increase or decrease time to fill in the recruitment process. Negative impact values indicate a reduction in time to fill, while positive values indicate an increase, i.e. issues with the process.",
    availableToLLM: true,
    availableToUser: false,
  },
  faq: {
    displayName: "FAQ",
    shortName: "FAQ",
    domoName: "View of Console_FAQ",
    description:
      "Frequently Asked Questions dataset containing common questions and answers related to the console and its data.",
    availableToLLM: false,
    availableToUser: false,
  },
  culture: {
    displayName: "Culture",
    shortName: "Culture",
    domoName: "View of ETL Output - Culture",
    description:
      "Dataset containing employee survey results, including employee feedback, cultural assessments, and related metrics.",
    availableToLLM: true,
    availableToUser: true,
  },
  employee_relations: {
    displayName: "Employee Relations",
    shortName: "Employee Relations",
    domoName: "View of Employee Relations",
    description:
      "Dataset containing information related to employee relations, including cases, investigations, and resolutions.",
    availableToLLM: true,
    availableToUser: true,
  },
  osha_incidents: {
    displayName: "OSHA Incidents",
    shortName: "OSHA Incidents",
    domoName: "View of OSHA Incidents",
    description:
      "Dataset containing information related to OSHA incidents, including incident reports, investigations, and compliance data.",
    availableToLLM: true,
    availableToUser: true,
  },
  employee_goals: {
    displayName: "Employee Goals",
    shortName: "Employee Goals",
    domoName: "View of Employee Goals",
    description:
      "Dataset containing employee goals and related performance metrics.",
    availableToLLM: true,
    availableToUser: true,
  },
  lightcast: {
    displayName: "Lightcast",
    shortName: "Lightcast",
    domoName: "View of lightcast_benchmark",
    description:
      "External Lightcast labor-market benchmarks by company-selected location, industry, occupation, and year. Use for questions about local versus national job supply and demand, hires, separations, openings, employment growth, wages and pay premiums, industry concentration, labor availability, and sector unemployment. Do not use for the company's own employees, applicants, vacancies, or internal turnover. Values are modeled market estimates; separation intensity measures exits per job, not resignation probability, and labor availability ratio is not the unemployment rate.",
    availableToLLM: true,
    availableToUser: true,
  },
};

export const LIKERT_SCALE_LABELS: Record<string, string> = {
  "1": "Strongly Disagree",
  "2": "Disagree",
  "3": "Neutral",
  "4": "Agree",
  "5": "Strongly Agree",
};

export const INSIGHTS_72_TYPE_LABELS: Record<string, string> = {
  "1": "Directing Reformer (Focused)",
  "21": "Directing Reformer (Classic)",
  "41": "Directing Reformer (Accommodating)",
  "101": "Creative Directing Reformer (Focused)",
  "121": "Creative Directing Reformer (Classic)",
  "141": "Creative Directing Reformer (Accommodating)",
  "2": "Reforming Director (Focused)",
  "22": "Reforming Director (Classic)",
  "42": "Reforming Director (Accommodating)",
  "102": "Creative Reforming Director (Focused)",
  "122": "Creative Reforming Director (Classic)",
  "142": "Creative Reforming Director (Accommodating)",
  "3": "Motivating Director (Focused)",
  "23": "Motivating Director (Classic)",
  "43": "Motivating Director (Accommodating)",
  "103": "Creative Motivating Director (Focused)",
  "123": "Creative Motivating Director (Classic)",
  "143": "Creative Motivating Director (Accommodating)",
  "4": "Directing Motivator (Focused)",
  "24": "Directing Motivator (Classic)",
  "44": "Directing Motivator (Accommodating)",
  "104": "Creative Directing Motivator (Focused)",
  "124": "Creative Directing Motivator (Classic)",
  "144": "Creative Directing Motivator (Accommodating)",
  "5": "Inspiring Motivator (Focused)",
  "25": "Inspiring Motivator (Classic)",
  "45": "Inspiring Motivator (Accommodating)",
  "105": "Creative Inspiring Motivator (Focused)",
  "125": "Creative Inspiring Motivator (Classic)",
  "145": "Creative Inspiring Motivator (Accommodating)",
  "6": "Motivating Inspirer (Focused)",
  "26": "Motivating Inspirer (Classic)",
  "46": "Motivating Inspirer (Accommodating)",
  "106": "Creative Motivating Inspirer (Focused)",
  "126": "Creative Motivating Inspirer (Classic)",
  "146": "Creative Motivating Inspirer (Accommodating)",
  "7": "Helping Inspirer (Focused)",
  "27": "Helping Inspirer (Classic)",
  "47": "Helping Inspirer (Accommodating)",
  "107": "Creative Helping Inspirer (Focused)",
  "127": "Creative Helping Inspirer (Classic)",
  "147": "Creative Helping Inspirer (Accommodating)",
  "8": "Inspiring Helper (Focused)",
  "28": "Inspiring Helper (Classic)",
  "48": "Inspiring Helper (Accommodating)",
  "108": "Creative Inspiring Helper (Focused)",
  "128": "Creative Inspiring Helper (Classic)",
  "148": "Creative Inspiring Helper (Accommodating)",
  "9": "Supporting Helper (Focused)",
  "29": "Supporting Helper (Classic)",
  "49": "Supporting Helper (Accommodating)",
  "109": "Creative Supporting Helper (Focused)",
  "129": "Creative Supporting Helper (Classic)",
  "149": "Creative Supporting Helper (Accommodating)",
  "10": "Helping Supporter (Focused)",
  "30": "Helping Supporter (Classic)",
  "50": "Helping Supporter (Accommodating)",
  "110": "Creative Helping Supporter (Focused)",
  "130": "Creative Helping Supporter (Classic)",
  "150": "Creative Helping Supporter (Accommodating)",
  "11": "Coordinating Supporter (Focused)",
  "31": "Coordinating Supporter (Classic)",
  "51": "Coordinating Supporter (Accommodating)",
  "111": "Creative Coordinating Supporter (Focused)",
  "131": "Creative Coordinating Supporter (Classic)",
  "151": "Creative Coordinating Supporter (Accommodating)",
  "12": "Supporting Coordinator (Focused)",
  "32": "Supporting Coordinator (Classic)",
  "52": "Supporting Coordinator (Accommodating)",
  "112": "Creative Supporting Coordinator (Focused)",
  "132": "Creative Supporting Coordinator (Classic)",
  "152": "Creative Supporting Coordinator (Accommodating)",
  "13": "Observing Coordinator (Focused)",
  "33": "Observing Coordinator (Classic)",
  "53": "Observing Coordinator (Accommodating)",
  "113": "Creative Observing Coordinator (Focused)",
  "133": "Creative Observing Coordinator (Classic)",
  "153": "Creative Observing Coordinator (Accommodating)",
  "14": "Coordinating Observer (Focused)",
  "34": "Coordinating Observer (Classic)",
  "54": "Coordinating Observer (Accommodating)",
  "114": "Creative Coordinating Observer (Focused)",
  "134": "Creative Coordinating Observer (Classic)",
  "154": "Creative Coordinating Observer (Accommodating)",
  "15": "Reforming Observer (Focused)",
  "35": "Reforming Observer (Classic)",
  "55": "Reforming Observer (Accommodating)",
  "115": "Creative Reforming Observer (Focused)",
  "135": "Creative Reforming Observer (Classic)",
  "155": "Creative Reforming Observer (Accommodating)",
  "16": "Observing Reformer (Focused)",
  "36": "Observing Reformer (Classic)",
  "56": "Observing Reformer (Accommodating)",
  "116": "Creative Observing Reformer (Focused)",
  "136": "Creative Observing Reformer (Classic)",
  "156": "Creative Observing Reformer (Accommodating)",
};
